"use client";

import React, { useState, useEffect, useContext } from "react";
import { Plus, Search, Package } from "lucide-react";
import "./ArticlePage.scss";
import DataGridServerSide from "../../Components/DataGridServerSide";
import ArticleGridHeader from "../../CRUD/Article/ArticleGridHeader";
import BarcodeGeneratorModal from "../../Components/GenerateBarcode";
import ArticleModal from "./ArticleModal";
import ArticleViewModal from "./ArticleViewModal";
import { ApiLink } from "../../APILink/ApiLink";
import axios from "axios";
import authHeader from "../../Auth/authHeader";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import { deleteArticle } from "./ArticleService";
import { handleSuccess, errorAlert, infoAlert } from "../../Components/Alerts";
import { AddAnElementButton } from "../../Components/AddElementsButton";
import CrudPageHeader from "../../Components/CrudPageHeader";
// import {
//   ElementsParagraph,
//   TitleElements,
// } from "../../Components/HeadersCRUD-s";

const getFriendlyDeleteMessage = (error) => {
  const data = error?.response?.data;
  const status = error?.response?.status;

  let rawMessage = "";
  if (typeof data === "string") rawMessage = data;
  else rawMessage = data?.Message || data?.message || error?.message || "";

  const normalized = String(rawMessage || "").trim();
  const text = normalized.toLowerCase();

  if (
    text.includes("artikull") &&
    (text.includes("historik") ||
      text.includes("transaksion") ||
      text.includes("dokument"))
  ) {
    return "Ky artikull nuk mund të fshihet sepse është përdorur në transaksione ose dokumente tjera.";
  }

  if (
    text.includes("foreign key") ||
    text.includes("reference constraint") ||
    text.includes("articleshistory") ||
    text.includes("sql") ||
    text.includes("exception")
  ) {
    return "Ky artikull nuk mund të fshihet sepse është i lidhur me të dhëna tjera në sistem.";
  }

  if (status === 404 || text.includes("nuk u gjet")) {
    return "Artikulli nuk u gjet ose është fshirë më herët.";
  }

  if (status >= 500 || text.includes("<!doctype") || text.includes("<html")) {
    return "Fshirja dështoi për shkak të një gabimi në server. Ju lutem provoni përsëri.";
  }

  if (!normalized) {
    return "Fshirja dështoi. Ju lutem provoni përsëri.";
  }

  if (normalized.length > 220) {
    return "Fshirja dështoi. Ju lutem provoni përsëri.";
  }

  return normalized;
};

export default function ArticlePage() {
  const headers = authHeader();
  const { fiscalYear } = useContext(FiscalYearContext);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewItemId, setViewItemId] = useState(null);

  const handleViewItem = (item) => {
    setViewItemId(item.ArticleID);
    setIsViewOpen(true);
  };

  //Kodi per filterat e datagridit
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  const [sortModel, setSortModel] = React.useState([]);
  const [filterModel, setFilterModel] = React.useState({ items: [] });

  const [quickFilter, setQuickFilter] = React.useState([]);

  const [open, setOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const handleOpenModal = (article) => {
    setSelectedArticle(article);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setSelectedArticle(null);
  };

  const handleAddItem = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item.ArticleID);
    setIsModalOpen(true);
  };

  const handleDeleteItem = async (deleteItemId) => {
    setLoading(true); // Optional: Disable buttons during process
    try {
      // 1. Execute the API call
      const response = await deleteArticle(deleteItemId);

      // 2. Success feedback (using the message from .NET Ok result)
      handleSuccess(response.message || "Artikulli u fshi me sukses!");
      fetchData();
    } catch (error) {
      errorAlert(getFriendlyDeleteMessage(error));
    } finally {
      setLoading(false);
    }
  };


  const fetchData = async () => {
    setLoading(true);

    try {
      const response = await axios.get(
        `${ApiLink.API_URL}Article/getArticlesGrid`,
        {
          headers,
          params: {
            fiscalYear: fiscalYear,
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
            sort: JSON.stringify(sortModel),
            filter: JSON.stringify(filterModel.items),
            quickFilter: quickFilter.join(" "), // dërgo si string
          },
        },
      );

      const transformedData = response.data.items.map((item) => ({
        ...item,
        ...item.Account,
      }));

      setData(transformedData);
      setRowCount(response.data.total);
    } catch (error) {
      // console.error("Gabim në kërkesë:", error);
    } finally {
      setLoading(false);
    }
  };

  const rows = data.map((item, index) => ({
    id: ++index,
    Barcode: item.Barcode,
    ArticleName: item.ArticleName,
    // articleType: "Artikull i Thjeshtë",
    ArticleProduction:
      item.ArticleProduction == null || item.ArticleProduction == 1
        ? "Artikull i Thjeshtë"
        : item.ArticleProduction == 2
          ? "Gjysëm Produkt"
          : "Produkt i Prodhuar",
    PurchasePrice: item.PurchasePrice,
    SalesPrice: item.SalesPrice,
    IsActive: item.IsActive,
    TouchScreen: item.TouchScreen,
    deleteItemId: item.ArticleID,
    rowInfo: item,
  }));

  useEffect(() => {
    fetchData();
  }, [paginationModel, sortModel, filterModel, quickFilter, fiscalYear]);

  return (
    <div className="ArticlePage__home-page">
      <CrudPageHeader
        icon={Package}
        title="Menaxhimi i Artikujve"
        description="Menaxhoni, shtoni, modifikoni dhe fshini artikujt e inventarit në mënyrë efikase."
        tutorialLabel="Shiko Tutorialin"
        onTutorialClick={() => infoAlert("Tutorial së shpejti!")}
        action={
          <AddAnElementButton
            className="AP__btn-primary"
            text="Artikull I Ri"
            icon={<Plus size={16} />}
            onClick={handleAddItem}
          />
        }
        className="AP__header"
      />
      {/* Modal */}
      <ArticleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchData}
        itemToEdit={editingItem}
      />

      <ArticleViewModal
        isOpen={isViewOpen}
        onClose={() => { setIsViewOpen(false); setViewItemId(null); }}
        articleId={viewItemId}
      />

      <DataGridServerSide
        // columns={ArticleGridHeader}
        columns={ArticleGridHeader({ handleOpenModal })}
        rows={rows}
        handleEdit={handleEditItem}
        handleDelete={handleDeleteItem}
        handleShowPrint={handleViewItem}
        showDetailsStatus={true}
        showPayment={false}
        height={500}
        rowCount={rowCount}
        loading={loading}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        sortModel={sortModel}
        setSortModel={setSortModel}
        filterModel={filterModel}
        setFilterModel={setFilterModel}
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
      />

      <BarcodeGeneratorModal
        open={open}
        handleClose={handleCloseModal}
        articleData={selectedArticle}
      />
    </div>
  );
}
